import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { mediaUrl } from '@/hooks/useMediaUrl';
import type { Album, Photo, PhotoTag } from '@shared/types';

interface FriendOption {
  id: string;
  display_name: string;
  username: string;
  avatar_key: string | null;
}

interface PhotoTagWithUser extends PhotoTag {
  display_name?: string;
  username?: string;
}

export function AlbumPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();

  const [album, setAlbum] = useState<Album | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState<Photo | null>(null);
  const [photoTags, setPhotoTags] = useState<PhotoTagWithUser[]>([]);
  const [showTagForm, setShowTagForm] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [tagResults, setTagResults] = useState<FriendOption[]>([]);
  const [tagSearching, setTagSearching] = useState(false);
  const [tagging, setTagging] = useState(false);

  const fetchAlbum = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await api.get<{ album: Album; photos: Photo[] }>(`/albums/${id}`);
      setAlbum(data.album);
      setPhotos(data.photos);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAlbum();
  }, [id]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('album_id', id);
      const photo = await api.upload<Photo>(`/albums/${id}/photos`, formData);
      setPhotos([photo, ...photos]);
    } catch {
      // ignore
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openLightbox = async (photo: Photo) => {
    setLightboxPhoto(photo);
    setShowTagForm(false);
    setTagSearch('');
    setTagResults([]);
    try {
      const tags = await api.get<PhotoTagWithUser[]>(`/photos/${photo.id}/tags`);
      setPhotoTags(tags);
    } catch {
      setPhotoTags([]);
    }
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
    setPhotoTags([]);
    setShowTagForm(false);
  };

  const searchFriends = async (query: string) => {
    setTagSearch(query);
    if (!query.trim()) {
      setTagResults([]);
      return;
    }
    setTagSearching(true);
    try {
      const results = await api.get<FriendOption[]>(`/friends/search?q=${encodeURIComponent(query.trim())}`);
      setTagResults(results);
    } catch {
      setTagResults([]);
    }
    setTagSearching(false);
  };

  const handleTag = async (friendId: string) => {
    if (!lightboxPhoto || tagging) return;
    setTagging(true);
    try {
      const tag = await api.post<PhotoTagWithUser>(`/photos/${lightboxPhoto.id}/tags`, { user_id: friendId });
      setPhotoTags([...photoTags, tag]);
      setShowTagForm(false);
      setTagSearch('');
      setTagResults([]);
    } catch {
      // ignore
    }
    setTagging(false);
  };

  const isOwner = album && user?.id === album.owner_id;

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-3 py-8 text-center text-sand-500">
        <div className="animate-pulse text-3xl mb-2">{'\u301c\ufe0f'}</div>
        <p className="text-sm">Loading album...</p>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="max-w-5xl mx-auto px-3 py-8 text-center text-sand-500">
        <div className="text-3xl mb-2">{'\ud83e\udd37'}</div>
        <p className="text-sm">Album not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-3 py-4">
      {/* Header */}
      <div className="card mb-4 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-sand-800">{album.name}</h1>
            {album.description && <p className="text-xs text-sand-600 mt-1">{album.description}</p>}
            <p className="text-[10px] text-sand-500 mt-1">{photos.length} photo{photos.length !== 1 ? 's' : ''}</p>
          </div>
          {isOwner && (
            <div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="btn-primary text-xs py-1.5 px-4 disabled:opacity-40"
              >
                {uploading ? 'Uploading...' : '&#128247; Upload Photo'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleUpload}
                className="hidden"
              />
            </div>
          )}
        </div>
      </div>

      {/* Photo grid */}
      {photos.length === 0 ? (
        <div className="text-center py-8 text-sand-500">
          <div className="text-3xl mb-2">&#128247;</div>
          <p className="text-sm">No photos in this album yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
          {photos.map((photo) => (
            <button
              key={photo.id}
              onClick={() => openLightbox(photo)}
              className="aspect-square rounded overflow-hidden bg-sand-200 hover:opacity-90 transition-opacity"
            >
              <img
                src={mediaUrl(photo.image_key)}
                alt={photo.caption || ''}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={closeLightbox}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <div className="flex justify-end p-2">
              <button onClick={closeLightbox} className="text-sand-400 hover:text-sand-600 text-lg">&#10005;</button>
            </div>

            {/* Image */}
            <div className="flex-1 min-h-0 px-4 flex items-center justify-center">
              <img
                src={mediaUrl(lightboxPhoto.image_key)}
                alt={lightboxPhoto.caption || ''}
                className="max-w-full max-h-[60vh] object-contain"
              />
            </div>

            {/* Info */}
            <div className="p-4 border-t border-sand-200">
              {lightboxPhoto.caption && (
                <p className="text-sm text-sand-800 mb-2">{lightboxPhoto.caption}</p>
              )}

              {/* Tags */}
              {photoTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  <span className="text-[10px] text-sand-500 mr-1">Tagged:</span>
                  {photoTags.map((tag) => (
                    <Link
                      key={tag.id}
                      to={`/profile/${tag.username}`}
                      onClick={closeLightbox}
                      className="text-[10px] px-1.5 py-0.5 bg-vibe-50 text-vibe-600 rounded font-bold hover:bg-vibe-100"
                    >
                      {tag.display_name || tag.username || 'User'}
                    </Link>
                  ))}
                </div>
              )}

              {/* Tag button */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowTagForm(!showTagForm)}
                  className="text-[10px] text-vibe-500 hover:text-vibe-600 font-bold"
                >
                  &#127991;&#65039; Tag a Friend
                </button>
                <span className="text-[10px] text-sand-400">
                  {lightboxPhoto.like_count > 0 && `${lightboxPhoto.like_count} likes`}
                  {lightboxPhoto.like_count > 0 && lightboxPhoto.comment_count > 0 && ' \u00b7 '}
                  {lightboxPhoto.comment_count > 0 && `${lightboxPhoto.comment_count} comments`}
                </span>
              </div>

              {/* Tag form */}
              {showTagForm && (
                <div className="mt-2">
                  <input
                    value={tagSearch}
                    onChange={(e) => searchFriends(e.target.value)}
                    placeholder="Search friends to tag..."
                    className="input w-full text-xs"
                    autoFocus
                  />
                  {tagResults.length > 0 && (
                    <div className="mt-1 border border-sand-300 rounded max-h-32 overflow-y-auto">
                      {tagResults.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => handleTag(f.id)}
                          disabled={tagging}
                          className="flex items-center gap-2 w-full px-2 py-1.5 hover:bg-sand-50 transition-colors text-left disabled:opacity-40"
                        >
                          <img src={mediaUrl(f.avatar_key)} alt="" className="w-5 h-5 rounded object-cover" />
                          <span className="text-xs font-bold text-sand-800">{f.display_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {tagSearching && <p className="text-[10px] text-sand-500 mt-1">Searching...</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
